import React from "react";
import { Set } from "immutable";

import { testImageOnloadAsync } from "../../../../Components/Apps/RetailMatch/Logic/SharedMethods";
import { createGenericIcon } from "../../../../Components/Common/GenericIcon/GenericIcon.Helpers";
import { IBrand } from "../../../../Models/IBrand";
import { IBrandSampleSet } from "../../../../Models/IBrandSampleSet";
import { IMapMarker, MapMarker } from "../../Models/IMapMarker";
import { IPolygon } from "../../Models/Maps/IPolygon";
import { PolygonType } from "../../Models/Maps/Enums/PolygonType";
import { ICircle } from "../../Models/Maps/ICircle";
import { IRectangle } from "../../Models/Maps/IRectangle";
import { ICustomPolygon } from "../../Models/Maps/ICustomPolygon";
import { circleToPoints, customPolygonToPoints, rectangleToPoints } from "../../Logic/PolygonLogic";
import { ILatLon } from "../../../../Models/ILatLon";

export const defaultCenter = new google.maps.LatLng(32.7079, -96.9209);
export const defaultZoom = 14;

export const getUniqueIdForBrand = (data: IBrandSampleSet): string => {
    return `${data.brandId}-${data.locationId}`;
};

export const findMatchingBrand = (brandSample: IBrandSampleSet, brandList: IBrand[]): IBrand => {
    return brandList.find((b: IBrand) => {
        return b.brandId === brandSample.brandId;
    });
};

export const convertBrandToMarker = (brandSample: IBrandSampleSet, matchingBrand: IBrand): IMapMarker => {
    return new MapMarker(
        getUniqueIdForBrand(brandSample),
        brandSample.latitude,
        brandSample.longitude,
        matchingBrand.logoPath,
        false,
        false,
        25,
        25,
        (
            <>
                <div>{brandSample.brandName}</div>
                <ul>
                    <li>City: {brandSample.city}</li>
                    <li>State: {brandSample.state}</li>
                    <li>City: {brandSample.zipcode}</li>
                </ul>
            </>
        ),
        false,
        brandSample.brandName,
        true,
        brandSample.brandId,
    );
};

const mapGoodBrandsToMarkers = (
    sampleSet: IBrandSampleSet[],
    brands: IBrand[],
    excludedMarkers: Set<string>,
): IMapMarker[] => sampleSet
    .filter(sample => !excludedMarkers.contains(sample.locationId))
    .map((data: IBrandSampleSet) => {
        const matchingBrand: IBrand = findMatchingBrand(data, brands);
        return matchingBrand !== undefined
            && typeof data.latitude === 'number'
            && typeof data.longitude === 'number'
            && convertBrandToMarker(data, matchingBrand);
    })
    .filter(x => !!x); // filter out markers that aren't there

export const getMapMarkersFromBrandSampleSet = (
    sampleSet: IBrandSampleSet[],
    brands: IBrand[],
    excludedMarkers: Set<string>,
): Promise<IMapMarker[]> => {
    const brandsInSampleSet: IBrand[] = brands.filter(b => sampleSet.findIndex(s => s.brandId === b.brandId) >= 0);
    const testLogoPromises: Promise<void>[] = brandsInSampleSet.map(brand =>
        testImageOnloadAsync(brand.logoPath).then(async (validSrc) => {
            if (!validSrc) {
                await createGenericIcon(brand.brandName).then(
                    (createdLogo) => (brand.logoPath = createdLogo)
                );
            }
        }));
    return Promise
        .all(testLogoPromises)
        .then(() => mapGoodBrandsToMarkers(sampleSet, brands, excludedMarkers));
};

export const getPointsFromPolygonType = (polygon: IPolygon): ILatLon[] => {
    switch (polygon.polygonType){
        case PolygonType.Circle: {
            const poly = polygon as ICircle;
            return circleToPoints(poly);
        }
        case PolygonType.Rectangle: {
            const poly = polygon as IRectangle;
            return rectangleToPoints(poly);
        }
        case PolygonType.Custom: {
            const poly = polygon as ICustomPolygon;
            return customPolygonToPoints(poly);
        }
        case PolygonType.Unknown:
        default: {
            return [];
        }
    }
};
